import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Truck, MapPin, Clock, CheckCircle2, AlertCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { fmtMoney, fmtTimeAgo } from '@/lib/realtime';

const STATUS_LABEL = {
  open: { label: 'Available', cls: 'border border-amber-300 bg-amber-50 text-amber-900' },
  accepted: { label: 'Accepted', cls: 'border border-emerald-300 bg-emerald-50 text-emerald-900' },
  expired: { label: 'Expired', cls: 'border border-zinc-300 bg-zinc-50 text-zinc-700' },
  cancelled: { label: 'Cancelled', cls: 'border border-zinc-300 bg-zinc-50 text-zinc-700' },
};

export default function DispatchAcceptPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user, isAuthed } = useAuth();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [accepting, setAccepting] = useState(false);
  const [acceptedByMe, setAcceptedByMe] = useState(false);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.get(`/delivery/dispatch/${token}`);
      setState({ loading: false, data, error: null });
    } catch (e) {
      setState({ loading: false, data: null, error: e?.response?.data?.detail || 'Dispatch not found' });
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const accept = async () => {
    if (!isAuthed) {
      // Hand off to login then return
      nav(`/login?next=${encodeURIComponent(`/dispatch/${token}`)}`);
      return;
    }
    setAccepting(true);
    try {
      await api.post(`/delivery/dispatch/${token}/accept`);
      toast.success('Dispatch accepted! Heading to your queue…');
      setAcceptedByMe(true);
      setTimeout(() => nav('/driver'), 1200);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || 'Failed to accept';
      if (status === 409) toast.error('Already taken by another driver');
      else if (status === 410) toast.error('Dispatch is no longer available');
      else toast.error(detail);
      await load();
    } finally {
      setAccepting(false);
    }
  };

  if (state.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (state.error || !state.data) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="flex flex-col items-center gap-2 p-8 text-center">
            <XCircle className="h-10 w-10 text-rose-500" />
            <p className="font-display text-xl">Dispatch unavailable</p>
            <p className="text-sm text-muted-foreground">{state.error || 'This link is invalid or expired.'}</p>
            <Button className="mt-4" onClick={() => nav('/driver')}>Go to driver app</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { dispatch, order_summary: order, branch } = state.data;
  const statusInfo = STATUS_LABEL[dispatch.status] || STATUS_LABEL.open;
  const open = dispatch.status === 'open';
  const youAcceptedAlready = dispatch.status === 'accepted' && dispatch.accepted_by === user?.id;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-primary text-primary-foreground">
        <div className="mx-auto flex max-w-md items-center justify-between px-4 py-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] opacity-80">Delivery dispatch</p>
            <h1 className="font-display text-lg font-semibold">#{order?.order_number || '—'}</h1>
          </div>
          <Badge data-testid="dispatch-status-badge" className={statusInfo.cls}>{statusInfo.label}</Badge>
        </div>
      </header>
      <main className="mx-auto max-w-md space-y-4 px-4 py-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Truck className="h-5 w-5 text-primary" /> Order details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Order total</span>
              <span className="font-display text-lg font-semibold tabular">{fmtMoney(order?.total, order?.currency)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Items</span>
              <span className="font-medium">{order?.item_count || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Payment</span>
              <span className="capitalize">{(order?.payment_method || '').replace('_', ' ') || '—'} · {order?.payment_status || '—'}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <MapPin className="h-4 w-4 text-primary" /> Pickup & drop-off
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {branch && (
              <div>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Pickup</p>
                <p className="font-medium">{branch.name}</p>
                <p className="text-xs text-muted-foreground">{branch.address || '—'}</p>
              </div>
            )}
            <div>
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Drop-off area</p>
              <p className="font-medium">{order?.drop_off_area || '—'}</p>
              <p className="text-xs text-muted-foreground">Full address shown after you accept.</p>
            </div>
            <div className="flex items-center gap-1 pt-1 text-[11px] text-muted-foreground">
              <Clock className="h-3 w-3" /> Created {fmtTimeAgo(order?.created_at)} · Strategy: {dispatch.strategy}
            </div>
          </CardContent>
        </Card>

        {open && !acceptedByMe && (
          <Button
            data-testid="dispatch-accept-button"
            className="w-full text-lg"
            size="lg"
            onClick={accept}
            disabled={accepting}
          >
            {accepting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Accepting…</> : <><CheckCircle2 className="mr-2 h-5 w-5" /> Accept this delivery</>}
          </Button>
        )}
        {(dispatch.status === 'accepted' && !youAcceptedAlready && !acceptedByMe) && (
          <Card className="border-rose-300 bg-rose-50">
            <CardContent className="flex items-center gap-2 p-3 text-sm text-rose-900">
              <AlertCircle className="h-4 w-4" /> This delivery was already accepted by another driver. Stay ready — the next one is coming!
            </CardContent>
          </Card>
        )}
        {(dispatch.status === 'expired' || dispatch.status === 'cancelled') && (
          <Card className="border-zinc-300 bg-zinc-50">
            <CardContent className="flex items-center gap-2 p-3 text-sm text-zinc-700">
              <AlertCircle className="h-4 w-4" /> This dispatch is no longer available.
            </CardContent>
          </Card>
        )}
        {(youAcceptedAlready || acceptedByMe) && (
          <Card className="border-emerald-300 bg-emerald-50">
            <CardContent className="flex items-center gap-2 p-3 text-sm text-emerald-900">
              <CheckCircle2 className="h-4 w-4" /> You’ve accepted this delivery.
              <Button size="sm" className="ml-auto" onClick={() => nav('/driver')}>Open driver app</Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
